import { Component, Input, forwardRef, Output, EventEmitter, OnDestroy, OnInit, ElementRef} from '@angular/core';
import { FormControl } from '@angular/forms';
import { BaseControlValueAccessor } from '../base-control-value-accessor';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-select-sectioned',
  templateUrl: './select-sectioned.component.html',
  styleUrls: ['../../styles/form-input.scss', './select-sectioned.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectSectionedComponent),
      multi: true
    }
  ]
})
export class SelectSectionedComponent extends BaseControlValueAccessor<any> implements OnInit, OnDestroy {

  @Input() formControl: FormControl;
  @Input() config: any;
  @Input() placeholder: string;
  @Input() showClear: boolean;
  @Input() type = 'dropdown';
  @Input() getCustomOptionValue: (arg0: any) => string;
  @Input() getCustomOptionLabel: (arg0: any) => string;
  @Output() changeCallback = new EventEmitter();

  public canSearch: boolean;
  public canAdd: boolean;
  public isListShown = false;
  private searchQuery = '';
  public displayValue: string;
  private watcher: any;
  private optionLists: any[];
  public activeOption: any;
  public activeList: any;

  constructor(private elementRef: ElementRef) {
    super();
  }

  @Input()
  set referenceValue(referenceValue: any) {
    if (!this.formControl) {
      this.setFormControlValue(referenceValue);
    }
  }

  @Input()
  set options(options: any) {
    this.optionLists = options || [];
    this.filterOptions();
  }

  ngOnInit() {
    this.setOptions();
    if (this.formControl) {
      this.formControl.markAsUntouched();
      this.watcher = this.formControl.valueChanges.subscribe(this.onFormControlChange.bind(this));
      if (this.formControl.value) {
        this.setFormControlValue(this.formControl.value);
      }
    }
  }

  setFormControlValue(value: string) {
    if (value) {
      let optionFound = false;
      for (const optionList of this.optionLists) {
        for (const option of optionList.options) {
          if (this.getOptionValue(option) && value &&
              (this.getOptionValue(option).toLowerCase() === value.toLowerCase()) &&
              this.value !== this.getOptionValue(option)) {
            this.setValue(this.getOptionValue(option));
            optionFound = true;
          }
        }
      }
      if (!optionFound && value !== this.value) {
        this.setValue(value);
      }
    }
    this.setDisplayValue();
  }

  onFormControlChange = (): void => {
    this.setFormControlValue(this.formControl.value);
  }

  setDisplayValue(): void {
    if (this.value) {
      for (const optionList of this.optionLists) {
        for (const option of optionList.options) {
          if (this.getOptionValue(option) === this.value) {
            this.displayValue = this.getOptionLabel(option);
            return;
          }
        }
      }
    }
    this.displayValue = this.value;
  }

  setValue(value: string) {
    this.value = value;
    this.onChange(this.value);
    this.setDisplayValue();
    if (this.changeCallback) {
      this.changeCallback.emit(this.value);
    }
  }

  onOptionClick(option: any): void {
    this.setValue(this.getOptionValue(option));
    this.hideList();
  }

  getOptionValue(option: any): string {
    if (this.getCustomOptionValue) {
      return this.getCustomOptionValue(option);
    }
    return option;
  }

  getOptionLabel(option: any): string {
    if (this.getCustomOptionLabel) {
      return this.getCustomOptionLabel(option);
    }
    return option;
  }

  showError(): boolean {
    return Boolean(this.formControl && this.formControl.errors && this.formControl.dirty);
  }

  setOptions(): void {
    switch (this.type) {
      case 'dropdown':
        this.canSearch = false; this.canAdd = false;
        this.placeholder = this.placeholder || 'Select';
        break;
      case 'dropdownAndInput':
        this.canSearch = false; this.canAdd = true;
        this.placeholder = this.placeholder || 'Select or enter';
        break;
      case 'dropdownAndSearch':
        this.canSearch = true; this.canAdd = false;
        this.placeholder = this.placeholder || 'Select';
        break;
      case 'dropdownAndSearchAndInput':
        this.canSearch = true; this.canAdd = true;
        this.placeholder = this.placeholder || 'Select or enter';
        break;
      default: console.warn('Incorrect dropdown type', this.type);
    }

  }

  handleTriggerClick(e: Event) {
    if (this.formControl && this.formControl.disabled) {
      return;
    }
    if (!this.isListShown) {
      this.showList();
    } else if (this.isListShown && !this.canAdd) {
      this.hideList();
    }
  }

  handleInputChange(event ?: any) {
    if (this.canSearch) {
      this.searchQuery = this.displayValue;
      this.filterOptions();
    }
    if (this.canAdd) {
      this.setValue(this.getValueFromDisplayValue());
    }
    if (event && event?.keyCode === 13 && typeof this.activeOption === 'number') {
      this.onOptionClick(this.optionLists[this.activeList].filteredOptions[this.activeOption]);
    }  else if (event?.keyCode === 13 && this.isListShown) {
      this.hideList();
    } else if (!event || event.keyCode !== 27) {
      this.showList();
    }
  }

  preventEnterKeyBehaviour(event) {
    // enter key triggers a click outside event by default
    if (event.keyCode === 13) {
      event.preventDefault();
    }
  }

  getValueFromDisplayValue(): string {
    const displayValue = this.displayValue.toLowerCase().trim();
    for (const optionList of this.optionLists) {
      for (const option of optionList.options) {
        if (this.getOptionLabel(option).toLowerCase() === displayValue) {
          return this.getOptionValue(option);
        }
      }
    }
    return this.canAdd ? this.displayValue :  '';
  }

  filterOptions() {
    for (const optionList of this.optionLists) {
      optionList.filteredOptions = optionList.options.filter((option) => {
        const label = this.getOptionLabel(option).toLowerCase();
        return label.indexOf(this.searchQuery.toLowerCase()) > -1;
      });
    }
  }

  handleInputBlur() {
    if (!this.value) {
      this.displayValue = '';
      this.handleInputChange();
    }
  }

  showList() {
    this.isListShown = true;
  }

  hideList() {
    this.activeOption = undefined;
    this.activeList = undefined;
    this.isListShown = false;
  }

  clickOutside() {
    this.isListShown = false;
  }

  clearInput(event) {
    event.stopPropagation();
    this.setValue('');
  }

  handleKeyDown(event) {
    if (event && event.keyCode === 40) {
      event.preventDefault();
      this.setActiveOption(1);
    } else if (event && event.keyCode === 38) {
      event.preventDefault();
      this.setActiveOption(-1);
    } else if (event && (event.keyCode === 9 || event.keyCode === 27)) {
      this.hideList();
    }
  }

  setActiveOption(incrementor: number) {
    if (this.activeList === undefined && this.activeOption === undefined) {
      this.activeOption = 0;
      this.activeList = 0;
    } else if (incrementor === 1) {
      if (this.optionLists[this.activeList].filteredOptions.length - 1 > this.activeOption) {
        this.activeOption++;
      } else if (this.optionLists.length - 1 > this.activeList) {
        this.activeOption = 0;
        this.activeList++;
      } else {
        this.activeOption = 0;
        this.activeList = 0;
      }
    } else if (incrementor === -1) {
      if (this.activeOption > 0) {
        this.activeOption--;
      } else if (this.activeList > 0) {
        this.activeList--;
        this.activeOption = this.optionLists[this.activeList].filteredOptions.length - 1;
      } else {
        this.activeList = this.optionLists.length - 1;
        this.activeOption = this.optionLists[this.activeList].filteredOptions.length - 1;
      }
    }
    this.scrollToActiveOption();
  }


  scrollToActiveOption() {
    const parent = this.elementRef.nativeElement;
    parent.getElementsByClassName('option-list')[this.activeList]
      .getElementsByClassName('select-option')[this.activeOption].scrollIntoView({block: 'nearest'});
  }

  checkInput(event){
    return ((event.charCode >= 97 && event.charCode <= 122) || (event.charCode >= 64 && event.charCode <= 90) || ((event.charCode >= 48 && event.charCode <= 57)) || (event.charCode == 32) || (event.charCode == 39)  || (event.charCode >= 44 && event.charCode <= 47 ));
  }
  validateInput(event) {
    const clipboardData = event.clipboardData || window.Clipboard;
    const pastedText = clipboardData.getData('text');
    const specialCharsRegex = /[!#$%^&*()?":{}|<>]/;
    if (specialCharsRegex.test(pastedText)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  ngOnDestroy() {
    if (this.watcher) {
      this.watcher.unsubscribe();
    }
    if (this.formControl) {
      this.formControl.updateValueAndValidity({emitEvent: false});
    }
  }
}
